import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Bug,
  Camera,
  Copy,
  Loader2,
  Lock,
  RefreshCcw,
  Search,
  Star,
  Trash2,
  X,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import PageShell from "@/components/PageShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const Stars = ({ value }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={13}
        className={n <= value ? "text-[#F4C752]" : "text-white/25"}
        fill={n <= value ? "#F4C752" : "transparent"}
      />
    ))}
  </div>
);

const AdminBetaFeedback = () => {
  const [params, setParams] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [tokenInput, setTokenInput] = useState(params.get("token") || "");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // full detail
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | bugs | feedback
  const [search, setSearch] = useState("");

  const fetchAll = async (t) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const [listRes, statsRes] = await Promise.all([
        fetch(
          `${BACKEND_URL}/api/admin/beta-feedback?token=${encodeURIComponent(t)}`
        ),
        fetch(
          `${BACKEND_URL}/api/admin/beta-feedback/stats?token=${encodeURIComponent(
            t
          )}`
        ),
      ]);
      if (listRes.status === 401 || statsRes.status === 401) {
        setError("Invalid admin token.");
        setList([]);
        setStats(null);
        setLoading(false);
        return;
      }
      const [listJson, statsJson] = await Promise.all([
        listRes.json(),
        statsRes.json(),
      ]);
      setList(Array.isArray(listJson) ? listJson : []);
      setStats(statsJson);
    } catch (e) {
      setError("Could not load feedback. Check network and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const applyToken = (e) => {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) return;
    setToken(t);
    setParams({ token: t });
  };

  const viewDetail = async (id) => {
    setDetailLoading(true);
    setSelected({ id });
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/beta-feedback/${id}?token=${encodeURIComponent(
          token
        )}`
      );
      if (!res.ok) {
        toast.error("Could not load submission.");
        setSelected(null);
        return;
      }
      const data = await res.json();
      setSelected(data);
    } finally {
      setDetailLoading(false);
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    const res = await fetch(
      `${BACKEND_URL}/api/admin/beta-feedback/${id}?token=${encodeURIComponent(
        token
      )}`,
      { method: "DELETE" }
    );
    if (res.ok) {
      toast.success("Deleted.");
      setSelected(null);
      fetchAll(token);
    } else {
      toast.error("Delete failed.");
    }
  };

  const filtered = useMemo(() => {
    let items = list;
    if (filter === "bugs") items = items.filter((i) => i.is_bug);
    if (filter === "feedback") items = items.filter((i) => !i.is_bug);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (i) =>
          (i.message || "").toLowerCase().includes(q) ||
          (i.tester_email || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [list, filter, search]);

  // ===== Token entry gate =====
  if (!token) {
    return (
      <PageShell navyTheme={true}>
        <div className="mx-auto max-w-md px-4 md:px-6 py-16 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
          >
            <ArrowLeft size={14} /> Back to SpendNova
          </Link>
          <form
            onSubmit={applyToken}
            className="mt-6 rounded-3xl p-6 md:p-8"
            style={{
              background:
                "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 40px 80px -40px rgba(0,0,0,0.55)",
            }}
            data-testid="admin-token-form"
          >
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#B7A5FF] font-bold">
              <Lock size={13} /> Admin cockpit
            </div>
            <h1
              className="mt-3 text-2xl font-extrabold text-white leading-tight"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              Enter your admin token
            </h1>
            <p className="mt-2 text-[13px] text-[#C7D0DF]">
              Only the SpendNova operator has this token. It's set in the
              backend .env file as <code>ADMIN_TOKEN</code>.
            </p>
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="paste admin token"
              autoFocus
              className="mt-4 w-full px-4 py-3 rounded-xl text-white bg-white/5 border focus:outline-none focus:ring-2 focus:ring-[#B7A5FF]"
              style={{
                borderColor: "rgba(255,255,255,0.20)",
                fontFamily: "Source Code Pro, ui-monospace, monospace",
              }}
              data-testid="admin-token-input"
            />
            <button
              type="submit"
              className="mt-4 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl text-[15px] font-semibold text-white"
              style={{
                background: "#B7A5FF",
                color: "#04111F",
                boxShadow:
                  "0 10px 30px -12px rgba(183,165,255,0.55), inset 0 1px 0 rgba(255,255,255,0.30)",
              }}
              data-testid="admin-token-submit"
            >
              Enter cockpit
            </button>
          </form>
        </div>
      </PageShell>
    );
  }

  // ===== Cockpit =====
  return (
    <PageShell navyTheme={true}>
      <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#B7A5FF] font-bold">
              <Lock size={13} /> Admin cockpit
            </div>
            <h1
              className="mt-2 text-2xl md:text-3xl font-extrabold text-white"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              Beta Feedback
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchAll(token)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-white"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.20)",
              }}
              data-testid="admin-refresh"
            >
              <RefreshCcw size={13} /> Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                setToken("");
                setParams({});
                setTokenInput("");
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-[#EF6B6B]"
              style={{
                background: "rgba(239,107,107,0.10)",
                border: "1px solid rgba(239,107,107,0.40)",
              }}
              data-testid="admin-logout"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { k: "Total", v: stats.total },
              { k: "Bugs", v: stats.bugs, tone: "#EF6B6B" },
              { k: "Avg rating", v: stats.avg_rating || "—", tone: "#F4C752" },
              { k: "With screenshot", v: stats.with_screenshots, tone: "#B7A5FF" },
            ].map((c) => (
              <div
                key={c.k}
                className="rounded-2xl p-4"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <div className="text-[10px] uppercase tracking-[0.20em] text-white/60 font-bold">
                  {c.k}
                </div>
                <div
                  className="mt-1 text-2xl font-extrabold"
                  style={{
                    fontFamily: "Sora, Inter, sans-serif",
                    color: c.tone || "#FFFFFF",
                  }}
                >
                  {c.v}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filter + search */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {[
            { k: "all", label: "All" },
            { k: "bugs", label: "Bugs" },
            { k: "feedback", label: "Feedback" },
          ].map((f) => {
            const active = filter === f.k;
            return (
              <button
                key={f.k}
                type="button"
                onClick={() => setFilter(f.k)}
                className="px-3 py-1.5 rounded-full text-[12px] font-semibold"
                style={{
                  background: active ? "#22C585" : "rgba(255,255,255,0.06)",
                  color: active ? "#04111F" : "#FFFFFF",
                  border: active
                    ? "1px solid rgba(34,197,133,0.75)"
                    : "1px solid rgba(255,255,255,0.20)",
                }}
                data-testid={`admin-filter-${f.k}`}
              >
                {f.label}
              </button>
            );
          })}
          <div
            className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.20)",
            }}
          >
            <Search size={14} className="text-white/60" />
            <input
              type="text"
              placeholder="Search message or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent text-[13px] text-white outline-none w-48"
              data-testid="admin-search"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            className="mt-4 rounded-xl px-4 py-3 text-[13px] text-[#EF6B6B]"
            style={{
              background: "rgba(239,107,107,0.10)",
              border: "1px solid rgba(239,107,107,0.40)",
            }}
          >
            {error}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="mt-10 flex items-center justify-center text-white/70">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading feedback…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="mt-10 rounded-2xl p-8 text-center"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px dashed rgba(255,255,255,0.20)",
            }}
            data-testid="admin-empty"
          >
            <MessageSquare
              size={22}
              className="mx-auto text-white/40 mb-2"
            />
            <div className="text-white/70 text-sm">
              No feedback yet. Submissions will appear here in real time.
            </div>
          </div>
        ) : (
          <div className="mt-6 grid gap-3">
            {filtered.map((f) => (
              <button
                type="button"
                key={f.id}
                onClick={() => viewDetail(f.id)}
                className="text-left rounded-2xl p-4 md:p-5 hover:-translate-y-[1px] transition-transform"
                style={{
                  background:
                    "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                  border: f.is_bug
                    ? "1px solid rgba(239,107,107,0.35)"
                    : "1px solid rgba(255,255,255,0.12)",
                }}
                data-testid={`admin-item-${f.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Stars value={f.rating} />
                      {f.is_bug && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest"
                          style={{
                            background: "rgba(239,107,107,0.14)",
                            border: "1px solid rgba(239,107,107,0.45)",
                            color: "#EF6B6B",
                          }}
                        >
                          <Bug size={10} /> Bug
                        </span>
                      )}
                      {f.has_screenshot && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest"
                          style={{
                            background: "rgba(183,165,255,0.14)",
                            border: "1px solid rgba(183,165,255,0.40)",
                            color: "#B7A5FF",
                          }}
                        >
                          <Camera size={10} /> Screenshot
                        </span>
                      )}
                      {f.tester_email && (
                        <span className="text-[11px] text-[#22C585] font-semibold">
                          {f.tester_email}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-[14px] text-white leading-snug line-clamp-2">
                      {f.message}
                    </div>
                  </div>
                  <div className="text-[11px] text-white/50 shrink-0 text-right">
                    {formatDate(f.created_at)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(4,17,31,0.75)", backdropFilter: "blur(6px)" }}
          onClick={() => setSelected(null)}
          data-testid="admin-detail-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl rounded-3xl p-6 md:p-7"
            style={{
              background: "linear-gradient(160deg, #10233C 0%, #04111F 100%)",
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 60px 100px -40px rgba(0,0,0,0.7)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.20)",
              }}
              data-testid="admin-detail-close"
            >
              <X size={16} />
            </button>
            {detailLoading || !selected.message ? (
              <div className="py-10 flex items-center justify-center text-white/70">
                <Loader2 size={16} className="animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <Stars value={selected.rating} />
                  {selected.is_bug && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest"
                      style={{
                        background: "rgba(239,107,107,0.14)",
                        border: "1px solid rgba(239,107,107,0.45)",
                        color: "#EF6B6B",
                      }}
                    >
                      <Bug size={10} /> Bug
                    </span>
                  )}
                  <span className="text-[11px] text-white/50 ml-auto">
                    {formatDate(selected.created_at)}
                  </span>
                </div>
                <div className="mt-4 text-[15px] text-white whitespace-pre-wrap leading-relaxed">
                  {selected.message}
                </div>
                {selected.tester_email && (
                  <div className="mt-3 flex items-center gap-2 text-[13px]">
                    <span className="text-white/60">Reply to:</span>
                    <a
                      href={`mailto:${selected.tester_email}`}
                      className="text-[#22C585] font-semibold hover:underline"
                      data-testid="admin-detail-email"
                    >
                      {selected.tester_email}
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard
                          .writeText(selected.tester_email)
                          .then(() => toast.success("Email copied."));
                      }}
                      className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white"
                    >
                      <Copy size={11} /> copy
                    </button>
                  </div>
                )}
                {selected.screenshot_base64 && (
                  <div
                    className="mt-4 rounded-2xl overflow-hidden"
                    style={{ border: "1px solid rgba(255,255,255,0.15)" }}
                    data-testid="admin-detail-screenshot"
                  >
                    <img
                      src={selected.screenshot_base64}
                      alt="Tester screenshot"
                      className="w-full h-auto"
                    />
                  </div>
                )}
                {selected.device_info && (
                  <details className="mt-4">
                    <summary className="text-[11px] text-white/60 cursor-pointer">
                      Device info
                    </summary>
                    <div
                      className="mt-2 text-[11px] text-white/70 font-mono break-all rounded-lg p-3"
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      {selected.device_info}
                    </div>
                  </details>
                )}
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => deleteOne(selected.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-[#EF6B6B]"
                    style={{
                      background: "rgba(239,107,107,0.10)",
                      border: "1px solid rgba(239,107,107,0.40)",
                    }}
                    data-testid="admin-detail-delete"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default AdminBetaFeedback;
